import type { FC } from 'react';

import { Rendering, useContent, usePlaceholder, useRendering, useSetting } from '@eneco/cms';
import { Bleed, Carousel as SparkyCarousel } from '@eneco/sparky';

import type { CarouselContent } from './carousel-content.types';
import { CAROUSEL_SLOTS, resolveSlot } from './slots';

type SparkyCarouselProps = Parameters<typeof SparkyCarousel>[0];

/**
 * `Carousel` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Slides are placed individually — `usePlaceholder` for the list, `<Rendering>` per slide — rather
 * than handed over as one `<Placeholder>` group. Sparky needs both: a `Carousel.Item` per slide for
 * scroll snapping, and a real children array to count for the indicator.
 *
 * **`showIndicator` is opt-in, not opt-out.** Sparky forces `scroll-snap-type: x mandatory` whenever
 * it is on (`Carousel.tsx`: `scrollBehavior: showIndicator ? 'snap' : scrollBehavior`), which locks
 * free scrolling — so defaulting it on made every carousel unscrollable by wheel. The legacy
 * component reads it as `!!Number(params?.showIndicator)`, i.e. absent means off, and this matches.
 *
 * `maxHeight` is not mapped: the legacy component pushes it onto child `Image` renderings by mutating
 * their params, which the agnostic registry gives no channel for (a component only ever sees its own
 * content).
 */
const Carousel: FC = () => {
  const content = useContent<CarouselContent>();
  const rendering = useRendering();

  // Sitecore rendering parameters are stringly-typed `'0'`/`'1'`, matching the legacy component's
  // own `!!Number(...)` reads rather than treating any non-empty string as true.
  const showIndicator = Boolean(Number(useSetting('showIndicator') ?? 0));
  const hideArrows = Boolean(Number(useSetting('hideArrows') ?? 0));
  const startAt = Number(useSetting('startAt') ?? 0) || 0;
  const layout = (useSetting('layout') ?? 'default') as SparkyCarouselProps['layout'];

  const mainSlot = resolveSlot(rendering, CAROUSEL_SLOTS.main);
  const blocks = usePlaceholder(mainSlot ?? CAROUSEL_SLOTS.main[0]);
  if (!mainSlot) return null;

  const slides = blocks.map(block => (
    <SparkyCarousel.Item key={block.uid}>
      <Rendering rendering={block} />
    </SparkyCarousel.Item>
  ));

  // Ignored when the indicator is on (Sparky forces `snap` there). With it off, mobile snaps and
  // desktop scrolls freely — the combination that makes a wheel or trackpad work.
  const scrollBehavior: SparkyCarouselProps['scrollBehavior'] = showIndicator
    ? 'native'
    : { initial: 'snap', md: 'vertical' };

  // Sparky types the label/arrow props as a union discriminated on `showIndicator`, so the two cases
  // are separate elements rather than a spread — the props genuinely do not exist without it.
  return (
    <Bleed horizontal="gridGutter">
      {showIndicator ? (
        <SparkyCarousel
          showIndicator
          hideArrows={hideArrows}
          startAt={startAt}
          layout={layout}
          scrollBehavior={scrollBehavior}
          scrollMarginX="gridGutter"
          hasCardPadding
          prevLabel={content.previousButtonText}
          nextLabel={content.nextButtonText}>
          {slides}
        </SparkyCarousel>
      ) : (
        <SparkyCarousel
          startAt={startAt}
          layout={layout}
          scrollBehavior={scrollBehavior}
          scrollMarginX="gridGutter"
          hasCardPadding>
          {slides}
        </SparkyCarousel>
      )}
    </Bleed>
  );
};

export default Carousel;
